import Loader from "common/Loader";
import _uniqBy from "lodash/uniqBy";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";

import events from "js/events";
import { MATCH_SCORE_THRESHOLD, STORAGE_KEY } from "lib/config";
import { PHONE_STATE, PROFILE_STRENGTH } from "lib/constants";
import { positionShape, profileCompletenessShape, questionShape } from "lib/propTypes";
import { calculateMatchScore, formatPhone } from "lib/util";
import wsStorage from "lib/wsStorage";
import { candidateFetchProfile } from "redux/candidate/actions";

import SummaryInvites from "./Summary/SummaryInvites";
import SummaryMismatches from "./Summary/SummaryMismatches";
import SummaryProfileCard from "./Summary/SummaryProfileCard";
import SummaryQuestions from "./Summary/SummaryQuestions";
import SummaryRedirect from "./Summary/SummaryRedirect";
import SummarySimilar from "./Summary/SummarySimilar";
import SummarySubmits from "./Summary/SummarySubmits";
import { hasMismatch, isMismatch } from "./Summary/util";

function canShowJobAlerts() {
    const hasDeclined = wsStorage.getItem(STORAGE_KEY.SUMMARY_JOB_PROMO) || false;
    return !hasDeclined && window.phoneState === PHONE_STATE.VERIFIED;
}

const mapStateToProps = (state) => ({
    autoApplyEnabled: state.autoApplyEnabled,
    loading: state.ws.ui.loading,
    completeness: state.profile ? state.profile.completeness : null,
});

class CandidateApplicationSummary extends React.PureComponent {
    static propTypes = {
        activePIDs: PropTypes.arrayOf(PropTypes.number).isRequired,
        answerCount: PropTypes.number.isRequired,
        autoApplyEnabled: PropTypes.bool,
        candidateFetchProfile: PropTypes.func.isRequired,
        completeness: profileCompletenessShape,
        inviteCount: PropTypes.number.isRequired,
        jobAlertsEnabled: PropTypes.bool,
        loading: PropTypes.bool.isRequired,
        positions: PropTypes.arrayOf(positionShape).isRequired,
        questions: PropTypes.arrayOf(questionShape).isRequired,
        phone: PropTypes.string.isRequired,
    };

    static defaultProps = {
        autoApplyEnabled: null,
        jobAlertsEnabled: false,
        completeness: null,
    };

    constructor(...args) {
        super(...args);

        const { positions } = this.props;

        const submittedPositions = positions.filter((p) => !isMismatch(p));
        const mismatchedPositions = positions.filter((p) => isMismatch(p));
        const similarPositions = submittedPositions
            .filter((p) => !isMismatch(p))
            .reduce((memo, p) => _uniqBy(memo.concat(p.similar_positions || []), "id"), []);

        this.state = {
            enabledAutoApply: false,
            mismatchedPositions,
            similarPositions,
            submittedPositions,
        };
    }

    componentDidMount() {
        const { activePIDs, completeness } = this.props;
        const { submittedPositions } = this.state;

        if (!completeness) {
            this.props.candidateFetchProfile();
        }

        let passingApps = 0;
        submittedPositions.forEach((p) => {
            if (!hasMismatch(p)) {
                passingApps += 1;
                events.trackPassingApp();
            }

            if (activePIDs.indexOf(p.id) === -1) {
                events.track("FUNNEL", "NEW_APPLICATION");
            }
        });

        if (submittedPositions.length > 0 && window.gtag) {
            window.gtag("event", "purchase", {
                job_id: submittedPositions.map((p) => p.id.toString()),
                job_pagetype: "conversion",
            });
        }

        if (passingApps) {
            events.trackAppSession();
        }

        events.trackPando("apply");
        events.trackStep("HARD_REVIEW");
    }

    UNSAFE_componentWillReceiveProps({ autoApplyEnabled }) {
        // eslint-disable-line
        const { autoApplyEnabled: current, completeness } = this.props;

        if (
            current === null &&
            autoApplyEnabled &&
            completeness &&
            completeness.strength !== PROFILE_STRENGTH.EXCELLENT
        ) {
            this.setState({ enabledAutoApply: true });

            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
        }
    }

    renderQuestions() {
        const { answerCount, autoApplyEnabled, jobAlertsEnabled, questions, phone } = this.props;
        const { submittedPositions } = this.state;

        return (
            <SummaryQuestions
                answerCount={answerCount}
                autoApplyEnabled={autoApplyEnabled}
                showJobAlertsPromo={!jobAlertsEnabled && canShowJobAlerts()}
                questions={questions}
                submittedCount={submittedPositions.length}
                phone={formatPhone(phone)}
            />
        );
    }

    // Mocked out in storybook
    renderCountdown() {
        return <SummaryRedirect />;
    }

    renderCTA() {
        const {
            answerCount,
            autoApplyEnabled,
            completeness,
            inviteCount,
            jobAlertsEnabled,
            loading,
            questions,
        } = this.props;
        const { submittedPositions } = this.state;

        if (submittedPositions.length === 0) {
            return null;
        }

        if (inviteCount > 0) {
            return <SummaryInvites />;
        }

        if (autoApplyEnabled === null || (!jobAlertsEnabled && canShowJobAlerts())) {
            return this.renderQuestions();
        }

        if (!completeness && loading) {
            return (
                <div className="app-summary__loading-cta">
                    <Loader loading />
                </div>
            );
        }

        if (completeness && completeness.strength !== PROFILE_STRENGTH.EXCELLENT) {
            return <SummaryProfileCard />;
        }

        const matchesUnlocked = calculateMatchScore(answerCount) >= MATCH_SCORE_THRESHOLD;
        if (questions.length > 0 && !matchesUnlocked) {
            return this.renderQuestions();
        }

        const { similarPositions } = this.state;
        if (similarPositions.length > 0) {
            return <SummarySimilar positions={similarPositions} />;
        }

        return this.renderCountdown();
    }

    render() {
        const { enabledAutoApply, mismatchedPositions, submittedPositions } = this.state;

        let body;
        if (enabledAutoApply) {
            body = (
                <div className="app-summary__section app-summary__section--content app-summary__auto-apply-section">
                    <header>
                        Auto-apply is <em>on</em>
                    </header>

                    <p>
                        To get the most success out of auto-apply, double-check that your Profile is
                        where you want it to be!
                    </p>
                </div>
            );
        } else {
            const { activePIDs } = this.props;

            body = (
                <>
                    <SummarySubmits activePIDs={activePIDs} positions={submittedPositions} />
                    <SummaryMismatches
                        activePIDs={activePIDs}
                        positions={mismatchedPositions}
                        hasSubmittedApps={submittedPositions.length > 0}
                    />
                </>
            );
        }

        return (
            <div className="app-summary">
                {body}
                {this.renderCTA()}
            </div>
        );
    }
}

export default connect(mapStateToProps, {
    candidateFetchProfile,
})(CandidateApplicationSummary);
